/**
 * Employee Web — `/me/expenses/$id` editor / read-only route.
 *
 * Phase 19 Plan 01. Renders MeExpenseEditor; UUID-shape guarded so non-UUID
 * URLs 404 deterministically. Permission gate: `expense:read:self`.
 */
import type { JSX } from "react";
import { z } from "zod";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { requirePermission } from "@linkhr/auth";
import { MeExpenseEditor } from "../features/expenses/MeExpenseEditor";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const permissionGate = requirePermission({
  anyOf: ["expense:read:self"],
  defaultRoute: "/me/overview",
});

const searchSchema = z.object({
  mode: z.enum(["new"]).optional(),
});

export const Route = createFileRoute("/_auth/me/expenses/$id")({
  staticData: { breadcrumb: { labelKey: "expenses:me.expenses.page.title" } },
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: (args) => {
    if (!UUID_REGEX.test(args.params.id)) {
      throw notFound();
    }
    permissionGate(args);
  },
  component: MeExpenseDetailRoute,
});

function MeExpenseDetailRoute(): JSX.Element {
  const { id } = Route.useParams();
  return <MeExpenseEditor reportId={id} />;
}
